import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheckCircle } from '#src/atoms/icons/system/iconCheckCircle.ts';
import { iconPlus } from '#src/atoms/icons/system/iconPlus.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Snackbar } from '#src/atoms/snackbar/Snackbar/index.tsx';
import { BrandM, LabelM } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { TextField } from '#src/components/form/Form/index.tsx';
import { useEffectOnChange } from '#src/hooks/state/useEffectOnChange.ts';
import { glossary } from '#src/lib/glossary.ts';

import { useEnterCode } from './useEnterCode.ts';

const DialogWrapper = styled(Vertical).attrs({ gap: 4 })`
  padding: 0 var(--quadruple-unit) var(--quadruple-unit);
`;

export const EnterCodeButton = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    inputValue,
    setInputValue,
    resetInputValue,
    error,
    successSnackbarOpen,
    setSuccessSnackbarOpen,
    validateAndApplyCode,
    alreadyApplied,
  } = useEnterCode();

  const onConfirm = async () => {
    const { valid } = await validateAndApplyCode();
    if (valid) {
      setSuccessSnackbarOpen(true);
      setInputValue('');
      setDialogOpen(false);
    }
  };

  // reset input value on next time the dialog opens
  useEffectOnChange(dialogOpen, () => {
    if (dialogOpen) {
      resetInputValue();
    }
  });

  return (
    <>
      <Button
        size="small"
        color="transparent"
        startIcon={
          <FontAwesomeIcon
            icon={alreadyApplied ? iconCheckCircle : iconPlus}
            color={alreadyApplied ? 'var(--c-green-300)' : undefined}
          />
        }
        onClick={() => setDialogOpen(true)}
      >
        {alreadyApplied ? (
          <FormattedMessage defaultMessage="Code added" />
        ) : (
          <FormattedMessage defaultMessage="Add a code" />
        )}
      </Button>
      <Dialog
        open={dialogOpen}
        fullWidth={false}
        maxWidth="md"
        fullScreen={false}
        onClose={() => {
          setDialogOpen(false);
        }}
      >
        <DialogWrapper>
          <BrandM>
            <FormattedMessage defaultMessage="Enter a valid code" />
          </BrandM>
          <Vertical gap={2}>
            <TextField
              autoFocus
              label={<FormattedMessage defaultMessage="Code" />}
              value={inputValue}
              onChange={e => setInputValue(e.target.value.toUpperCase())}
              name="code"
            />
            {!!error && <LabelM color="var(--c-red-400)">{error}</LabelM>}
            <Button
              color="primary"
              size="medium"
              onClick={onConfirm}
              disabled={!inputValue.trim()}
            >
              <FormattedMessage {...glossary.confirm} />
            </Button>
          </Vertical>
        </DialogWrapper>
      </Dialog>
      <Snackbar
        open={successSnackbarOpen}
        onClose={() => setSuccessSnackbarOpen(false)}
        message={<FormattedMessage defaultMessage="Code activated" />}
      />
    </>
  );
};
